import { Box, ConfirmationDialog, Divider, Stack } from "@tm/components"
import { useWorkTask, WorkTaskInfo } from "@tm/context-distribution"
import { useDeleteNote, useSaveNote } from "@tm/utils"
import { useCallback, useEffect, useRef, useState } from "react"
import { AddCustomPartListRequest, CustomPart, Note, NoteTypes } from "@tm/models"
import {
    EditCustomArticleItemRequest,
    AddCustomArticleItemsRequest,
    useEditCustomArticleItem,
    CustomArticleItemResponse,
    useAddCustomArticleItem,
    useEditCustomArticleList,
    useCreateCustomArticleList,
    useDeleteCustomArticleLists,
} from "@tm/data/basket"
import { useWorkTaskBasketState } from "@bundles/basket"
import { mapCustomArticleItemToCustomPart } from "@bundles/compilations/data/mapper/customItems"
import { useLocalization } from "@tm/localization"
import { ModuleHeader } from "./ModuleHeader"
import { DialogCustomArticle, ArticleEditDialog } from "./ArticleEditDialog"
import { ArticleListHandle, OwnArticleList } from "./ArticleList"
import { CategoryFilter, useOwnArticlesStore } from "./store"
import { CompilationList } from "./CompilationList"
import { CompilationDetails } from "./CompilationDetails"
import { CompilationEditDialog, DialogCustomList } from "./CompilationEditDialog"
import { CompilationTable } from "./CompilationTable"

type OwnArticlesProps = {
    categoryFilter?: CategoryFilter
    onClose?(): void
    onReplaceArticle?(selected: CustomArticleItemResponse): void
}

export function OwnArticles(props: OwnArticlesProps) {
    const { workTask } = useWorkTask() ?? {}
    if (!workTask) {
        return null
    }
    return <OwnArticlesComponent workTask={workTask} {...props} />
}

function OwnArticlesComponent({ onClose, categoryFilter, onReplaceArticle, workTask }: OwnArticlesProps & { workTask: WorkTaskInfo }) {
    const { translateText } = useLocalization()
    const [saveError, setSaveError] = useState<string>()
    const {
        articleToEdit,
        setArticleToEdit,
        setIsSaving,
        compilationToEdit,
        setCompilationToEdit,
        isExpandedCompilationsList,
        compilationIdsToDelete,
        setCompilationIdsToDelete,
        setCategoryFilter,
        setCompilations,
        setIsExpandedCompilationsList,
        setSelectedCompilation,
    } = useOwnArticlesStore()

    const [editNote, setEditNote] = useState<Note>()

    const { basket } = useWorkTaskBasketState(workTask.id)
    const { addCustomParts } = basket.actions

    const { mutateAsync: saveEditedArticle } = useEditCustomArticleItem()
    const { mutateAsync: saveNewArticle } = useAddCustomArticleItem()

    const { mutateAsync: editList } = useEditCustomArticleList()
    const { mutateAsync: createList } = useCreateCustomArticleList()
    const { mutateAsync: deleteLists } = useDeleteCustomArticleLists()

    const { saveNote } = useSaveNote()
    const { deleteNote } = useDeleteNote()

    const listHandle = useRef<ArticleListHandle>(null)

    useEffect(() => {
        setCategoryFilter(categoryFilter)
    }, [categoryFilter])

    const handleEditArticle = useCallback((article: CustomArticleItemResponse, note?: Note) => {
        const selectedVatRate = article.vatRates.find((vatRate) => vatRate.isSelected) ?? article.vatRates[0]
        const { id, description, version, articleNumber, categoryId, isDefaultForCategory, purchasePrice, retailPrice, customArticleListId } = article
        setArticleToEdit({
            id,
            description,
            version,
            articleNumber,
            categoryId,
            isDefaultForCategory,
            purchasePrice,
            retailPrice,
            selectedVatRate,
            customArticleListId,
            type: "edit",
        })
        setEditNote(note)
    }, [])

    const handleCloseArticleDialog = useCallback(() => {
        setArticleToEdit(undefined)
        setSaveError(undefined)
    }, [])

    const handleAddToBasket = useCallback(
        async (items: [CustomArticleItemResponse, number][]) => {
            const addRequest: AddCustomPartListRequest = {
                customParts: items.map<CustomPart>(([article, quatnity]) => mapCustomArticleItemToCustomPart(article, quatnity, undefined)),
                workTaskId: workTask.id,
                vehicleId: workTask.vehicle?.id,
                customerId: workTask.customer?.id,
            }
            addCustomParts(addRequest)
        },
        [addCustomParts, workTask.customer?.id, workTask.id, workTask.vehicle?.id]
    )

    const handleSaveArticle = useCallback(
        async (article: DialogCustomArticle, note?: string) => {
            try {
                setIsSaving(true)
                setSaveError(undefined)
                if (article.type === "edit") {
                    const request: EditCustomArticleItemRequest = {
                        customArticleItemToEdit: article,
                    }

                    const result = await saveEditedArticle(request)

                    if (result && note !== editNote?.text) {
                        if (note) {
                            const noteRequest: Note = editNote
                                ? { ...editNote, text: note }
                                : { type: NoteTypes.CUSTOM_ARTICLE, text: note, noteId: result.editedCustomArticleItem.id }
                            await saveNote(noteRequest)
                        } else if (editNote) {
                            await deleteNote(editNote.noteId)
                        }
                    }
                } else if (article.description) {
                    const { type, ...customArticleItemToAdd } = article
                    const request: AddCustomArticleItemsRequest = {
                        customArticleItemsToAdd: [customArticleItemToAdd],
                    }
                    const result = await saveNewArticle(request)
                    listHandle.current?.scrollToTop()
                    if (note && result?.addedCustomArticleItems.length === 1) {
                        const addedCustomArticle = result.addedCustomArticleItems[0]
                        const noteRequest: Note = {
                            type: NoteTypes.CUSTOM_ARTICLE,
                            text: note,
                            noteId: addedCustomArticle.id,
                        }
                        await saveNote(noteRequest)
                    }
                }
                handleCloseArticleDialog()
            } catch {
                setSaveError(translateText(13452))
            } finally {
                setIsSaving(false)
            }
        },
        [deleteNote, editNote, handleCloseArticleDialog, saveEditedArticle, saveNewArticle, saveNote]
    )

    const handleCloseCompilationDialog = useCallback(() => {
        setCompilationToEdit(undefined)
        setSaveError(undefined)
    }, [])

    const handleSaveCompilation = useCallback(async (compilation: DialogCustomList) => {
        try {
            setIsSaving(true)
            setSaveError(undefined)
            if (compilation.type === "edit") {
                const { type, ...customListToEdit } = compilation
                await editList({ customListToEdit })
            } else {
                const { type, ...customListToCreate } = compilation
                await createList({ customListToCreate })
            }
        } catch {
            setSaveError(translateText(13452))
        } finally {
            handleCloseCompilationDialog()
            setIsSaving(false)
        }
    }, [])

    const handleConfirmDelete = useCallback(async () => {
        try {
            if (compilationIdsToDelete?.length) {
                await deleteLists({ idList: compilationIdsToDelete })
            }
        } finally {
            setCompilationIdsToDelete(undefined)
        }
    }, [deleteLists, compilationIdsToDelete])

    const handleCloseDeleteConfirmation = useCallback(() => {
        setCompilationIdsToDelete(undefined)
    }, [])

    return (
        <Stack gap={2} flex={1} height="100%" position="relative">
            <ModuleHeader onClose={onClose} />
            {!isExpandedCompilationsList && (
                <Stack direction="row" gap={2} px={2} divider={<Divider orientation="vertical" />} minHeight={0} height="100%">
                    <CompilationList />
                    <Stack flex={1} gap={2}>
                        <CompilationDetails />
                        <Divider />
                        <OwnArticleList
                            ref={listHandle}
                            onEditArticle={handleEditArticle}
                            onAddArticlesToBasket={handleAddToBasket}
                            onReplaceArticle={onReplaceArticle}
                        />
                    </Stack>
                </Stack>
            )}
            {isExpandedCompilationsList && (
                <Box px={2} minHeight={0} height="100%">
                    <CompilationTable />
                </Box>
            )}
            <ArticleEditDialog
                onClose={handleCloseArticleDialog}
                onSave={handleSaveArticle}
                article={articleToEdit}
                note={editNote?.text}
                saveError={saveError}
            />
            <CompilationEditDialog
                compilation={compilationToEdit}
                onClose={handleCloseCompilationDialog}
                onSave={handleSaveCompilation}
                saveError={saveError}
            />
            <ConfirmationDialog
                open={!!compilationIdsToDelete?.length}
                confirmationButtonText={translateText(585)}
                cancelButtonText={translateText(584)}
                onConfirm={handleConfirmDelete}
                onCancel={handleCloseDeleteConfirmation}
            >
                {translateText(14083)}
            </ConfirmationDialog>
        </Stack>
    )
}
